import { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { C, fieldStyle, tooltipStyle } from "../theme";
import { formatDate, money } from "../lib/format";
import { ApiError } from "../lib/api/client";
import {
  createTask,
  getFinancialSummary,
  getMarginByClass,
  getMonthlyProfitTrend,
  listOverdueMembershipPayments,
  listTasks,
  updateTask,
} from "../lib/api/endpoints";
import { useSession } from "../lib/session/SessionContext";
import { TASK_PRIORITY, TASK_STATUS, taskPriorityLabel, taskStatusLabel } from "../lib/enums";
import { canManageCourtPricing, canManageExpenses, canManageMembers, canManageTeam, canViewFinancials } from "../lib/access";
import { Shell } from "../components/Shell";
import { Card } from "../components/Card";
import { Label } from "../components/Label";
import { Button } from "../components/Button";
import { FieldInput } from "../components/Field";
import { FilterPills } from "../components/FilterPills";
import { KpiCard } from "../components/KpiCard";
import { WarehouseTab } from "../components/WarehouseTab";
import { CashierTab } from "../components/CashierTab";
import { PdvTab } from "../components/PdvTab";
import { MembersTab } from "../components/MembersTab";
import { PricingSettingsTab } from "../components/PricingSettingsTab";
import { BookingsAgendaTab } from "../components/BookingsAgendaTab";
import { ClassScheduleTab } from "../components/ClassScheduleTab";
import { CourtBlocksTab } from "../components/CourtBlocksTab";
import { CourtManagementTab } from "../components/CourtManagementTab";
import { SportManagementTab } from "../components/SportManagementTab";
import { PlanManagementTab } from "../components/PlanManagementTab";
import { ExpensesTab } from "../components/ExpensesTab";
import { MembershipPaymentsTab } from "../components/MembershipPaymentsTab";
import { WellhubCheckInsTab } from "../components/WellhubCheckInsTab";
import { TenantFinancialSettingsTab } from "../components/TenantFinancialSettingsTab";
import { PatchNotesTab } from "../components/PatchNotesTab";
import { TeamTab } from "../components/TeamTab";
import { DateTimePicker } from "../components/DateTimePicker";
import type { FinancialSummaryDto, MarginByClassDto, MonthlyProfitPointDto, OverdueMembershipPaymentDto, TaskItemDto } from "../types";

const ADMIN_TABS = ["Visão geral", "Financeiro", "Mensalidades", "Despesas", "Caixa", "Balcão", "Agenda", "Aulas", "Modalidades", "Planos", "Tarefário", "Armazém", "Alunos", "Wellhub / TotalPass", "Equipe", "Configurações", "Patch notes"];
const PRI_COR: Record<number, string> = { [TASK_PRIORITY.High]: C.red, [TASK_PRIORITY.Medium]: C.ink, [TASK_PRIORITY.Low]: C.light };
const TASK_COLS = [TASK_STATUS.ToDo, TASK_STATUS.InProgress, TASK_STATUS.Done] as const;
const MESES_ABREV = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

interface TaskDraft {
  title: string;
  priority: number;
  dueDate: string;
}

export function AdminScreen({
  onLogout,
  userName,
  userRole,
  ctName,
  tenantId,
}: {
  onLogout: () => void;
  userName: string;
  userRole: string;
  ctName: string;
  tenantId: string;
}) {
  const { accessToken } = useSession();
  const token = accessToken ?? "";
  const isCourtReader = userRole.toLowerCase() === "coach";
  const canSeeExpenses = canManageExpenses(userRole);
  const canManageCheckIns = canManageMembers(userRole);
  const canSeeFinancials = canViewFinancials(userRole);
  const tabs = isCourtReader
    ? ["Agenda", "Aulas", "Patch notes"]
    : ADMIN_TABS.filter(tab => {
        if (tab === "Despesas") return canSeeExpenses;
        if (tab === "Visão geral" || tab === "Financeiro" || tab === "Mensalidades") return canSeeFinancials;
        if (tab === "Wellhub / TotalPass") return canManageCheckIns;
        if (tab === "Equipe") return canManageTeam(userRole);
        if (tab === "Configurações") return canManageCourtPricing(userRole);
        return true;
      });
  const storageKey = `apogeu:admin-tab:${tenantId}`;
  const [tab, setTab] = useState(() => {
    const saved = window.localStorage.getItem(storageKey);
    return saved && tabs.includes(saved) ? saved : (isCourtReader ? "Agenda" : tabs[0] ?? "Agenda");
  });
  const [agendaView, setAgendaView] = useState("Reservas");

  useEffect(() => {
    if (tabs.includes(tab)) window.localStorage.setItem(storageKey, tab);
    else setTab(isCourtReader ? "Agenda" : tabs[0] ?? "Agenda");
  }, [isCourtReader, storageKey, tab, tabs]);

  const [summary, setSummary] = useState<FinancialSummaryDto | null>(null);
  const [trend, setTrend] = useState<MonthlyProfitPointDto[] | null>(null);
  const [overdue, setOverdue] = useState<OverdueMembershipPaymentDto[] | null>(null);
  const [margin, setMargin] = useState<MarginByClassDto[] | null>(null);
  const [financialError, setFinancialError] = useState<string | null>(null);

  const [finSummary, setFinSummary] = useState<FinancialSummaryDto | null>(null);
  const [finError, setFinError] = useState<string | null>(null);
  const [finReloadKey, setFinReloadKey] = useState(0);
  const [finMonth, setFinMonth] = useState(() => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  });

  const [tasks, setTasks] = useState<TaskItemDto[] | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [taskDraft, setTaskDraft] = useState<TaskDraft | null>(null);
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  useEffect(() => {
    if (!token || !canSeeFinancials) return;
    let cancelled = false;
    Promise.all([
      getFinancialSummary(token),
      getMonthlyProfitTrend(token, { months: 6 }),
      getMarginByClass(token),
      listOverdueMembershipPayments(token),
    ])
      .then(([s, t, m, o]) => {
        if (cancelled) return;
        setSummary(s);
        setTrend(t);
        setMargin(m);
        setOverdue(o);
      })
      .catch(e => {
        if (cancelled) return;
        setFinancialError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao carregar dados financeiros");
      });
    return () => { cancelled = true; };
  }, [token, tenantId, canSeeFinancials]);

  useEffect(() => {
    if (!token || !canSeeFinancials || tab !== "Financeiro") return;
    let cancelled = false;
    setFinSummary(null);
    setFinError(null);
    getFinancialSummary(token, { month: finMonth.month, year: finMonth.year })
      .then(s => {
        if (!cancelled) setFinSummary(s);
      })
      .catch(e => {
        if (cancelled) return;
        setFinError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao carregar o financeiro");
      });
    return () => { cancelled = true; };
  }, [token, tenantId, canSeeFinancials, tab, finMonth, finReloadKey]);

  const shiftFinMonth = (delta: number) => {
    setFinMonth(({ month, year }) => {
      const target = new Date(year, month - 1 + delta, 1);
      return { month: target.getMonth() + 1, year: target.getFullYear() };
    });
  };

  useEffect(() => {
    let cancelled = false;
    listTasks(token, { page: 1, pageSize: 100 })
      .then(result => {
        if (!cancelled) setTasks(result.items);
      })
      .catch(e => {
        if (cancelled) return;
        setTaskError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao carregar tarefas");
      });
    return () => { cancelled = true; };
  }, [token, tenantId, isCourtReader]);

  const advanceTask = (t: TaskItemDto) => {
    const next = (t.status + 1) as TaskItemDto["status"];
    if (next > TASK_STATUS.Done) return;
    setTasks(ts => ts?.map(x => (x.id === t.id ? { ...x, status: next } : x)) ?? null);
    updateTask(token, t.id, { status: next }).catch(() => {
      setTasks(ts => ts?.map(x => (x.id === t.id ? t : x)) ?? null);
    });
  };

  const submitTask = async () => {
    if (!token || !taskDraft || !taskDraft.title.trim()) return;
    setTaskSubmitting(true);
    try {
      const created = await createTask(token, {
        title: taskDraft.title.trim(),
        priority: taskDraft.priority,
        dueDate: taskDraft.dueDate || null,
      });
      setTasks(ts => [...(ts ?? []), created]);
      setTaskDraft(null);
    } catch (e) {
      setTaskError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao criar tarefa");
    } finally {
      setTaskSubmitting(false);
    }
  };

  const trendData = (trend ?? []).map(p => ({ mes: MESES_ABREV[p.month - 1] ?? String(p.month), lucro: p.profit }));
  const finMesLabel = `${MESES[finMonth.month - 1] ?? ""} ${finMonth.year}`;

  const hoje = new Date();
  const hojeIso = hoje.toISOString().slice(0, 10);
  const prazoLimiteIso = new Date(hoje.getTime() + 3 * 86400000).toISOString().slice(0, 10);
  const tarefasVencidas = (tasks ?? []).filter(t => t.status < TASK_STATUS.Done && t.dueDate && t.dueDate < hojeIso).length;
  const tarefasProximas = (tasks ?? []).filter(t => t.status < TASK_STATUS.Done && t.dueDate && t.dueDate >= hojeIso && t.dueDate <= prazoLimiteIso).length;
  const alertaTarefas: string[] = [];
  if (tarefasVencidas > 0) alertaTarefas.push(`${tarefasVencidas} ${tarefasVencidas === 1 ? "tarefa" : "tarefas"} em atraso`);
  if (tarefasProximas > 0) alertaTarefas.push(`${tarefasProximas} ${tarefasProximas === 1 ? "tarefa" : "tarefas"} com prazo para os próximos dias`);
  const textoAlertaTarefas = alertaTarefas.join(" e ");

  return (
    <Shell userName={userName} userRole={userRole} ctName={ctName} tabs={tabs} tab={tab} onTabChange={setTab} onLogout={onLogout}>
      {tab === "Visão geral" && canSeeFinancials && (
        <>
          {financialError ? (
            <Card>
              <Label style={{ color: C.red }}>Não foi possível carregar a visão geral</Label>
              <p style={{ fontSize: 13, color: C.gray, margin: "8px 0" }}>{financialError}</p>
              <Button onClick={() => setFinancialError(null)}>Tentar novamente</Button>
            </Card>
          ) : summary === null ? (
            <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Carregando visão geral...</div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
                <KpiCard label="Receita do mês" value={money(summary.revenue)} sub={`${summary.revenueBreakdown.length} fontes de receita`} />
                <KpiCard
                  label={summary.costBreakdown.some(item => item.name.toLocaleLowerCase("pt-BR") === "estoque") ? "Custos (despesas + estoque)" : "Custos"}
                  value={money(summary.costs)}
                  sub={`${summary.costBreakdown.length} categorias de custo`}
                />
                <KpiCard label={`Lucro de ${MESES[summary.month - 1]}`} value={money(summary.profit)} sub={`margem de ${summary.marginPct}%`} highlight />
              </div>

              <Card style={{ marginTop: 12 }}>
                <Label>Evolução do lucro</Label>
                {trendData.length === 0 ? (
                  <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Sem histórico suficiente para o gráfico.</div>
                ) : (
                  <div style={{ height: 180 }}>
                    <ResponsiveContainer>
                      <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                        <defs>
                          <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={C.lime} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={C.lime} stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="mes" tick={{ fontSize: 11, fill: C.light }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: C.light }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={v => money(Number(v))} contentStyle={tooltipStyle} />
                        <Area
                          type="monotone"
                          dataKey="lucro"
                          stroke={C.ink}
                          strokeWidth={2.4}
                          fill="url(#lg)"
                          dot={{ r: 0 }}
                          activeDot={{ r: 5, fill: C.lime, stroke: C.ink }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 12, marginTop: 12 }}>
                <Card>
                  <Label>⚠ Mensalidades atrasadas</Label>
                  {overdue === null ? (
                    <div style={{ color: C.light, fontSize: 13, padding: "12px 4px" }}>Carregando inadimplências...</div>
                  ) : overdue.length === 0 ? (
                    <div style={{ color: C.light, fontSize: 13, padding: "12px 4px" }}>Nenhuma mensalidade em atraso.</div>
                  ) : (
                    overdue.map(a => (
                      <div key={a.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.faint}`, fontSize: 13 }}>
                        <div>
                          <div style={{ fontWeight: 600, color: C.ink }}>{a.memberName}</div>
                          <div style={{ fontSize: 11, color: C.light }}>venceu em {formatDate(a.dueDate)}</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ color: C.red, fontWeight: 700 }}>{a.daysOverdue}d</div>
                          <div style={{ fontSize: 11, color: C.gray }}>{money(a.amount)}</div>
                          {a.whatsAppLink && (
                            <a href={a.whatsAppLink} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 700, color: C.ink, textDecoration: "none", borderBottom: `2px solid ${C.lime}` }}>
                              Cobrar no WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </Card>
              </div>
            </>
          )}
        </>
      )}

      {tab === "Financeiro" && canSeeFinancials && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <Button variant="ghost" onClick={() => shiftFinMonth(-1)} ariaLabel="Mês anterior" style={{ padding: "4px 10px" }}>←</Button>
            <Label style={{ margin: 0 }}>{finMesLabel}</Label>
            <Button variant="ghost" onClick={() => shiftFinMonth(1)} ariaLabel="Próximo mês" style={{ padding: "4px 10px" }}>→</Button>
          </div>
          {finError ? (
            <Card>
              <Label style={{ color: C.red }}>Não foi possível carregar o financeiro</Label>
              <p style={{ fontSize: 13, color: C.gray, margin: "8px 0" }}>{finError}</p>
              <Button onClick={() => setFinReloadKey(k => k + 1)}>Tentar novamente</Button>
            </Card>
          ) : finSummary === null ? (
            <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Carregando financeiro...</div>
          ) : (
            <>
              <Card>
                <Label>{finMesLabel} — receita x custos</Label>
                {[
                  ...finSummary.revenueBreakdown.map(r => ({ id: `r-${r.name}`, nome: r.name, valor: r.amount })),
                  ...finSummary.costBreakdown.map(c => ({ id: `c-${c.name}`, nome: c.name, valor: -c.amount })),
                ].map(line => (
                  <div key={line.id} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: `1px solid ${C.faint}`, fontSize: 13.5 }}>
                    <span style={{ color: C.gray }}>{line.nome}</span>
                    <span style={{ fontWeight: 700, color: line.valor > 0 ? C.ink : C.red }}>
                      {line.valor > 0 ? "+" : "−"} {money(Math.abs(line.valor))}
                    </span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 2px", fontSize: 15 }}>
                  <b style={{ color: C.ink }}>Lucro do mês</b>
                  <b style={{ color: C.ink, borderBottom: `3px solid ${C.lime}` }}>{money(finSummary.profit)}</b>
                </div>
              </Card>
              <Card style={{ marginTop: 12 }}>
                <Label>Margem por turma (R$/aluno)</Label>
                {margin === null ? (
                  <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Carregando margens...</div>
                ) : margin.length === 0 ? (
                  <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Nenhuma turma cadastrada ainda.</div>
                ) : (
                  <div style={{ height: 170 }}>
                    <ResponsiveContainer>
                      <BarChart data={margin.map(m => ({ t: m.className, m: m.revenuePerStudent }))} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                        <XAxis dataKey="t" tick={{ fontSize: 10, fill: C.light }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: C.light }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={v => "R$ " + Number(v)} contentStyle={tooltipStyle} />
                        <Bar dataKey="m" radius={[6, 6, 0, 0]}>
                          {margin.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? C.lime : C.ink} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </>
          )}
        </>
      )}

      {tab === "Mensalidades" && canSeeFinancials && <MembershipPaymentsTab />}

      {tab === "Agenda" && (
        <>
          <FilterPills options={isCourtReader ? ["Reservas", "Quadras"] : ["Reservas", "Horários e preços", "Bloqueios", "Quadras"]} value={agendaView} onChange={setAgendaView} style={{ marginBottom: 12 }} />
          {agendaView === "Reservas" && <BookingsAgendaTab key={`${tenantId}:bookings`} />}
          {agendaView === "Horários e preços" && !isCourtReader && <PricingSettingsTab key={`${tenantId}:pricing`} />}
          {agendaView === "Bloqueios" && !isCourtReader && <CourtBlocksTab key={`${tenantId}:court-blocks`} />}
          {agendaView === "Quadras" && <CourtManagementTab key={`${tenantId}:courts`} />}
        </>
      )}

      {tab === "Aulas" && <ClassScheduleTab />}

      {tab === "Modalidades" && <SportManagementTab key={`${tenantId}:sports`} />}

      {tab === "Planos" && <PlanManagementTab key={`${tenantId}:plans`} />}

      {tab === "Despesas" && canSeeExpenses && <ExpensesTab key={`${tenantId}:expenses`} />}

      {tab === "Tarefário" && (() => {
        const porColuna = (status: number) => (tasks ?? []).filter(t => t.status === status);
        return (
          <>
            {textoAlertaTarefas && (
              <Card style={{ borderColor: C.lime, background: C.limeSoft, marginBottom: 12 }}>
                <Label style={{ color: C.gray }}>🔔 Alertas do tarefário</Label>
                <p style={{ fontSize: 13, color: C.gray, margin: 0, lineHeight: 1.5 }}>
                  <b style={{ color: C.ink }}>{textoAlertaTarefas}</b> — confira o quadro abaixo.
                </p>
              </Card>
            )}
            {taskError && (
              <Card style={{ marginBottom: 12 }}>
                <Label style={{ color: C.red }}>Não foi possível carregar o tarefário</Label>
                <p style={{ fontSize: 13, color: C.gray, margin: "8px 0" }}>{taskError}</p>
                <Button onClick={() => setTaskError(null)}>Tentar novamente</Button>
              </Card>
            )}
            {tasks === null && !taskError ? (
              <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Carregando tarefas...</div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 12 }}>
                {TASK_COLS.map(status => (
                  <div key={status}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: C.ink, textTransform: "uppercase" }}>{taskStatusLabel(status)}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.gray, background: C.faint, borderRadius: 10, padding: "1px 8px" }}>
                        {porColuna(status).length}
                      </span>
                    </div>
                    {porColuna(status).map(t => (
                      <Card key={t.id} style={{ marginBottom: 8, padding: 14 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: status === TASK_STATUS.Done ? C.light : C.ink, textDecoration: status === TASK_STATUS.Done ? "line-through" : "none" }}>
                            {t.title}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: PRI_COR[t.priority] ?? C.light }}>{taskPriorityLabel(t.priority)}</span>
                        </div>
                        <div style={{ fontSize: 11, color: C.light, marginTop: 4 }}>
                          {t.dueDate ? formatDate(t.dueDate) : "Sem prazo"}
                        </div>
                        {status < TASK_STATUS.Done && (
                          <Button onClick={() => advanceTask(t)} style={{ marginTop: 8, fontSize: 11, padding: "5px 10px" }}>
                            {status === TASK_STATUS.ToDo ? "Iniciar →" : "Concluir ✓"}
                          </Button>
                        )}
                      </Card>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <Button
              style={{ marginTop: 12, fontSize: 12.5, padding: "9px 16px" }}
              onClick={() => setTaskDraft({ title: "", priority: TASK_PRIORITY.Medium, dueDate: "" })}
            >
              + Nova tarefa
            </Button>
            {taskDraft && (
              <Card style={{ marginTop: 12 }}>
                <Label>Nova tarefa</Label>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
                  <FieldInput
                    aria-label="Título da tarefa"
                    placeholder="O que precisa ser feito?"
                    value={taskDraft.title}
                    onChange={e => setTaskDraft(d => (d ? { ...d, title: e.target.value } : d))}
                    style={{ flex: 1, minWidth: 220 }}
                  />
                  <select
                    aria-label="Prioridade"
                    value={taskDraft.priority}
                    onChange={e => setTaskDraft(d => (d ? { ...d, priority: Number(e.target.value) } : d))}
                    style={fieldStyle}
                  >
                    <option value={TASK_PRIORITY.Low}>Prioridade baixa</option>
                    <option value={TASK_PRIORITY.Medium}>Prioridade média</option>
                    <option value={TASK_PRIORITY.High}>Prioridade alta</option>
                  </select>
                  <DateTimePicker
                    ariaLabel="Prazo"
                    value={taskDraft.dueDate}
                    onChange={dueDate => setTaskDraft(d => (d ? { ...d, dueDate } : d))}
                    mode="date"
                    placement="above"
                  />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <Button onClick={submitTask} disabled={taskSubmitting || !taskDraft.title.trim()}>
                    {taskSubmitting ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button variant="ghost" onClick={() => setTaskDraft(null)}>Cancelar</Button>
                </div>
              </Card>
            )}
          </>
        );
      })()}

      {/* key por tenant: ao trocar de CT, a aba é remontada e o estado por tenant
          (extrato do caixa, carrinho, comanda selecionada, listas) não persiste. */}
      {tab === "Armazém" && <WarehouseTab key={`${tenantId}:armazem`} />}

      {tab === "Caixa" && <CashierTab key={`${tenantId}:caixa`} />}

      {tab === "Balcão" && <PdvTab key={`${tenantId}:balcao`} />}

      {tab === "Alunos" && <MembersTab key={`${tenantId}:members`} />}
      {tab === "Equipe" && canManageTeam(userRole) && <TeamTab key={`${tenantId}:team`} />}

      {tab === "Wellhub / TotalPass" && canManageCheckIns && <WellhubCheckInsTab key={`${tenantId}:check-ins`} />}

      {tab === "Configurações" && canManageCourtPricing(userRole) && <TenantFinancialSettingsTab key={`${tenantId}:financial-settings`} />}

      {tab === "Patch notes" && <PatchNotesTab />}
    </Shell>
  );
}
